
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Paintbrush, Image, PlusCircle, LayoutGrid, Wand2 } from 'lucide-react';
import { useAuth } from '@/context/SupabaseAuthContext';
import { getAllBanners } from '@/utils/bannerService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const MarketingDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  
  // Get count of existing banners
  const bannerCount = getAllBanners().length;
  
  return (
    <AdminLayout isLoading={false} onLogout={signOut} userRole="admin">
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Marketing Dashboard</h1>
          <p className="text-gray-500">
            Create and manage marketing assets for your referral campaigns
          </p>
        </div>
        
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar Menu */}
          <div className="col-span-12 md:col-span-3">
            <Card className="sticky top-4">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-blue-500 text-white">
                <CardTitle className="flex items-center">
                  <Paintbrush className="h-5 w-5 mr-2" />
                  Marketing Tools
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="flex flex-col">
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/admin/marketing/create')}
                    className="justify-start rounded-none border-b h-14"
                  >
                    <PlusCircle className="mr-3 h-5 w-5" />
                    Create Banner
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/admin/marketing/ai-banner')}
                    className="justify-start rounded-none border-b h-14"
                  >
                    <Wand2 className="mr-3 h-5 w-5" />
                    AI Banner Creator
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/admin/marketing/manage')}
                    className="justify-start rounded-none border-b h-14"
                  >
                    <Image className="mr-3 h-5 w-5" />
                    Manage Banners
                    {bannerCount > 0 && (
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium rounded-full px-2 py-1">
                        {bannerCount}
                      </span>
                    )}
                  </Button>
                  <div className="p-4 text-sm text-gray-500">
                    <h3 className="font-medium mb-2">Banner Stats</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Total Banners:</span>
                        <span className="font-medium">{bannerCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Last Created:</span>
                        <span className="font-medium">
                          {bannerCount > 0 
                            ? new Date(Math.max(
                                ...getAllBanners().map(b => b.createdAt.getTime())
                              )).toLocaleDateString() 
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </nav>
              </CardContent>
            </Card>
          </div>
          
          {/* Main Content Area */}
          <div className="col-span-12 md:col-span-9">
            <Card>
              <CardHeader>
                <CardTitle>Getting Started with Marketing Tools</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">
                  Welcome to the marketing tools dashboard. From here, you can create and manage various marketing assets for your referral campaigns.
                </p>
                <p className="mb-6">
                  Click on any tool in the sidebar to begin creating or managing marketing assets.
                </p>
                
                <Tabs defaultValue="banners" className="w-full">
                  <TabsList className="w-full grid grid-cols-1 md:grid-cols-3 mb-6">
                    <TabsTrigger value="banners">Banner System</TabsTrigger>
                    <TabsTrigger value="email" disabled>Email Templates</TabsTrigger>
                    <TabsTrigger value="social" disabled>Social Media</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="banners" className="space-y-4">
                    <h3 className="text-lg font-medium">Banner Design System</h3>
                    <p>Create custom marketing banners for various platforms and ad networks. Our system supports multiple standard banner sizes and customization options.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="border p-4 rounded-md">
                        <h4 className="font-medium mb-2 flex items-center">
                          <PlusCircle className="h-4 w-4 mr-2" /> Create Banner
                        </h4>
                        <p className="text-sm text-gray-600">Design a new banner with our easy-to-use editor.</p>
                      </div>
                      <div className="border p-4 rounded-md">
                        <h4 className="font-medium mb-2 flex items-center">
                          <Wand2 className="h-4 w-4 mr-2" /> AI Banner Creator
                        </h4>
                        <p className="text-sm text-gray-600">Generate banners using AI-powered descriptions.</p>
                      </div>
                      <div className="border p-4 rounded-md">
                        <h4 className="font-medium mb-2 flex items-center">
                          <Image className="h-4 w-4 mr-2" /> Manage Banners
                        </h4>
                        <p className="text-sm text-gray-600">View, edit, and export your existing banners.</p>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-4">
                      <Button onClick={() => navigate('/admin/marketing/create')}>
                        Create Banner
                      </Button>
                      <Button onClick={() => navigate('/admin/marketing/ai-banner')} variant="outline">
                        Try AI Creator
                      </Button>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="email">
                    <p className="text-gray-500 italic">Email template features coming soon...</p>
                  </TabsContent>
                  
                  <TabsContent value="social">
                    <p className="text-gray-500 italic">Social media asset tools coming soon...</p>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MarketingDashboardPage;
